import { useState, useEffect } from 'react'
import { api, fixImageUrl } from '../api'
import { getZone } from '../theme'
import RinaContextCard from '../components/RinaContextCard'
import { useTheme } from '../theme'
import { useActionsPortail } from '../contexts/navigation'
import { useAuth } from '../AuthContext'
import { usePanier } from '../contexts/panier'
import PageHub from '../components/PageHub'

function ContenuFavoris() {
  const { isAuthenticated } = useAuth()
  const { basket, productFavs } = usePanier()
  const { navigate: onNavigate, setHubTab } = useActionsPortail()
  const onExplore = () => setHubTab('explore')
  const { isWeb3 } = useTheme()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const cardBg = 'bg-card border-card-border'

  const [orgs, setOrgs] = useState([])
  const [produits, setProduits] = useState([])
  // Demarrer a `true` en dur affichait la vue a onglets pendant ~20 ms avant de
  // basculer sur « Aucun favori » : une page differente, le temps d'un battement
  // de cil, a la PREMIERE ouverture de l'onglet (S28, ecart E-12).
  // Quand il n'y a rien a charger, il ne faut pas se declarer « en chargement ».
  const [loading, setLoading] = useState(() => basket.saved.length > 0)
  const [loadingProduits, setLoadingProduits] = useState(() => (productFavs?.saved?.length || 0) > 0)

  // Charger les details des organisations favorites
  useEffect(() => {
    if (basket.saved.length === 0) { setLoading(false); return }
    setLoading(true)
    Promise.all(
      basket.saved.map(orgId =>
        api.getProducteur(orgId).catch(() => null)
      )
    ).then(results => {
      setOrgs(results.filter(Boolean))
    }).finally(() => setLoading(false))
  }, [basket.saved])

  // Charger les details des offres produit favorites
  useEffect(() => {
    if (!productFavs || productFavs.saved.length === 0) { setLoadingProduits(false); return }
    setLoadingProduits(true)
    // Charger le marche pour obtenir les details des offres
    api.getMarches()
      .then(marches => {
        const firstMarche = Array.isArray(marches) && marches[0]
        if (!firstMarche?.id) return []
        return api.getMarcheDetail(firstMarche.id)
      })
      .then(detail => {
        if (!detail) { setProduits([]); return }
        const allOffers = (detail.stands || []).flatMap(st =>
          (st.offers || []).map(o => ({
            id: o.id,
            nom: o.product?.nom || o.nom || 'Produit',
            prix: o.prix,
            unite: o.unite,
            producteur: st.organization?.nomCommercial || st.organization?.nom || '',
            producteurId: st.organization?.id,
            categorie: o.product?.categorie,
          }))
        )
        // Filtrer pour ne garder que les favoris
        const favOffers = allOffers.filter(o => productFavs.isSaved(o.id))
        setProduits(favOffers)
      })
      .catch(() => setProduits([]))
      .finally(() => setLoadingProduits(false))
  }, [productFavs?.saved])

  // Variante VISITEUR (fidele prototype v5) : les favoris supposent un compte
  // -> carte d'invitation (bascule douce, jamais un mur).
  // ⚠️ Ce retour anticipe DOIT rester sous les hooks ci-dessus : place avant,
  // il changeait le nombre de hooks entre deux rendus au moment de la connexion
  // (regle des hooks React) — l'ecran pouvait alors se reconstruire entierement.
  if (!isAuthenticated) {
    return (
      <>
        {(
          <RinaContextCard
            tc={tc}
           
            message="Avec un compte, je garde vos favoris et vous préviens dès qu'ils reviennent."
          />
        )}
        <div
          className="rounded-3xl p-7 text-center mt-1 animate-fade-up"
          style={{ background: 'linear-gradient(160deg, rgba(255,255,255,0.6), rgba(244,137,83,0.1))', border: '1px solid rgba(255,255,255,0.85)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', boxShadow: '0 8px 30px rgba(7,62,61,0.08)' }}
        >
          <span className="inline-flex w-[60px] h-[60px] rounded-[20px] items-center justify-center text-[28px] mb-3.5"
            style={{ background: 'rgba(244,137,83,0.16)', border: '1px solid rgba(255,255,255,0.8)', boxShadow: 'inset 0 1px 3px rgba(255,255,255,0.9), 0 6px 16px rgba(244,137,83,0.18)' }}>⭐</span>
          <p className="font-serif text-[22px] text-ink mb-1.5">Vos favoris, au même endroit</p>
          <p className="text-[13px] text-ink/60 leading-relaxed mb-5 max-w-[280px] mx-auto">
            Avec un compte, gardez vos produits et producteurs préférés — je vous préviens dès qu'ils reviennent.
          </p>
          <button
            onClick={() => onNavigate?.('connexion')}
            className="px-8 py-3 rounded-full text-white text-sm font-extrabold"
            style={{ background: 'linear-gradient(120deg, rgba(244,137,83,0.9), rgba(224,101,58,0.92))', boxShadow: '0 10px 26px rgba(244,137,83,0.32), inset 0 1px 0 rgba(255,255,255,0.4)' }}
          >
            Créer un compte
          </button>
        </div>
      </>
    )
  }

  const totalCount = basket.count + (productFavs?.count || 0)

  // Les favoris arrivent du serveur APRES le premier rendu. Tant qu'ils ne sont
  // pas arrives, on ne sait pas s'ils sont vides — afficher « Aucun favori »
  // ici faisait clignoter un ecran entierement different avant la vraie liste,
  // a la premiere ouverture de l'onglet apres connexion (S28, ecart E-12).
  const favorisCharges = (basket.pret ?? true) && (productFavs?.pret ?? true)
  const isEmpty = favorisCharges && !loading && !loadingProduits && totalCount === 0
  // Tant que les favoris ne sont pas arrives, on montre le squelette plutot
  // qu'un « Aucun favori » qui serait dementi une fraction de seconde apres.
  const chargementProducteurs = loading || !favorisCharges
  const chargementProduits = loadingProduits || !favorisCharges

  // ATTENTE — un seul ecran, qui se LIT comme un chargement (S28, ecart E-12).
  //
  // Il ne suffit pas d'empecher « Aucun favori » de s'afficher trop tot : tant
  // que la reponse n'est pas la, afficher la vue a onglets puis basculer vers
  // l'ecran vide donne DEUX pages differentes a la suite — c'est le meme
  // clignotement, vu depuis l'autre bout. On rend donc un placeholder neutre
  // jusqu'a savoir, puis on va DIRECTEMENT a l'etat final (liste ou vide).
  if (!favorisCharges) {
    return (
      <>
        {(
          <RinaContextCard
            tc={tc}
           
            message="Je peux te recommander des producteurs que tu vas adorer !"
          />
        )}
        <div className="pt-2" aria-busy="true" aria-label="Chargement de vos favoris">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className={`h-[72px] rounded-2xl border mb-2.5 animate-pulse ${cardBg}`}
              style={{ animationDelay: `${i * 0.08}s` }}
            />
          ))}
        </div>
      </>
    )
  }

  if (isEmpty) {
    return (
      <>
        {(
          <RinaContextCard
            tc={tc}
           
            message="Je peux te recommander des producteurs que tu vas adorer !"
          />
        )}
        <div className="text-center pt-10 animate-fade-up">
          <span className="text-5xl block mb-3">⭐</span>
          <p className={`text-base font-bold mb-1.5 ${tc}`}>Aucun favori</p>
          <p className={`text-xs mb-5 ${ts}`}>Explore le marché pour découvrir des producteurs et produits !</p>
          <button
            onClick={onExplore}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-primary-soft)))' }}
          >
            Explorer le marché
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      {(
        <RinaContextCard
          tc={tc}
         
          message="Je peux te recommander d'autres producteurs que tu vas adorer !"
        />
      )}

      {/* UNE seule liste (proto v7.4) : producteurs et produits meles.
          Les onglets « Producteurs / Produits » du portail les separaient — mais
          on ne cherche pas ses favoris par type, on cherche ce qu'on a mis de
          cote. Le tri garde les producteurs en tete, ils sont moins nombreux. */}
      {chargementProducteurs || chargementProduits ? (
        <div className="space-y-2.5">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`h-[70px] rounded-[20px] animate-pulse ${isWeb3 ? 'bg-white/5' : 'bg-ink/5'}`} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {orgs.map((org, i) => (
            <div key={`o-${org.id || i}`}
              className={`flex items-center gap-3 px-[15px] py-[13px] rounded-[20px] border animate-fade-up ${cardBg}`}
              style={{ animationDelay: `${i * 0.04}s` }}>
              {org.logo ? (
                <img src={fixImageUrl(org.logo)} alt=""
                  className="w-[38px] h-[38px] rounded-[13px] object-cover flex-shrink-0" />
              ) : (
                <span className="text-[28px] flex-shrink-0 leading-none">🧑🏾‍🌾</span>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-[13.5px] font-bold truncate ${tc}`}>{org.nomCommercial || org.nom}</p>
                <p className={`text-[11px] truncate ${ts}`}>{org.zone ? getZone(org.zone).label : 'Producteur'}</p>
              </div>
              <button onClick={() => basket.toggle(org.id)} aria-label="Retirer des favoris"
                className="text-base flex-shrink-0" style={{ color: 'rgb(var(--c-primary))' }}>★</button>
            </div>
          ))}

          {produits.map((p, i) => (
            <div key={`p-${p.id || i}`}
              className={`flex items-center gap-3 px-[15px] py-[13px] rounded-[20px] border animate-fade-up ${cardBg}`}
              style={{ animationDelay: `${(orgs.length + i) * 0.04}s` }}>
              <span className="text-[28px] flex-shrink-0 leading-none">🥭</span>
              <div className="flex-1 min-w-0">
                <p className={`text-[13.5px] font-bold truncate ${tc}`}>{p.nom}</p>
                <p className={`text-[11px] truncate ${ts}`}>
                  {[p.producteur, p.prix ? `${p.prix} €${p.unite ? `/${p.unite}` : ''}` : null].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button onClick={() => productFavs.toggle(p.id)} aria-label="Retirer des favoris"
                className="text-base flex-shrink-0" style={{ color: 'rgb(var(--c-primary))' }}>★</button>
            </div>
          ))}

          {/* Favoris enregistres dont on n'a pas encore le detail : le produit
              n'est sur aucun marche publie. On le dit plutot que de l'effacer. */}
          {produits.length === 0 && (productFavs?.count || 0) > 0 && (
            <p className={`text-[11.5px] text-center py-2 ${ts}`}>
              {productFavs.count} produit{productFavs.count > 1 ? 's' : ''} en favori —
              le détail reviendra quand un évènement les proposera.
            </p>
          )}
        </div>
      )}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageFavoris() {
  return <PageHub><ContenuFavoris /></PageHub>
}
